import { useLocation, useNavigate, useParams } from 'react-router-dom'

interface GoToDriverParams {
  sortedIds: number[]
  numericId: number
}

const useChangeDriverParams = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { driverId } = useParams<{ driverId?: string }>()

  // Use the router-parsed current `driverId` as the needle instead of a
  // regex like `\/driver\/\d+/`. This keeps us in sync with what the
  // router sees and avoids the regex matching unintended segments.
  const goToDriver = (newId: number) => {
    if (!driverId) return
    const from = `/driver/${driverId}`
    const to = `/driver/${newId}`
    if (!location.pathname.includes(from)) return
    navigate(location.pathname.replace(from, to))
  }

  const goPrevDriverInActivity = ({ sortedIds, numericId }: GoToDriverParams) => {
    const prevId =
      [...sortedIds].reverse().find((id) => id < numericId) ??
      sortedIds[sortedIds.length - 1]
    goToDriver(prevId)
    return prevId
  }

  const goNextDriverInActivity = ({ sortedIds, numericId }: GoToDriverParams) => {
    const nextId = sortedIds.find((id) => id > numericId) ?? sortedIds[0]
    goToDriver(nextId)
    return nextId
  }

  // Replace one of several candidate URL segments — used by the date/month/year
  // store→URL sync effects. No-op if none of the candidates appear (the caller
  // may fire before the URL has been populated).
  const replaceUrlParam = ({
    paramNames,
    newValue,
  }: {
    paramNames: string[]
    newValue: string | number
  }) => {
    for (const paramName of paramNames) {
      const regex = new RegExp(`${paramName}/[^/]+`)
      if (regex.test(location.pathname)) {
        navigate(location.pathname.replace(regex, `${paramName}/${newValue}`))
        return
      }
    }
  }

  return { goPrevDriverInActivity, goNextDriverInActivity, replaceUrlParam }
}

export default useChangeDriverParams
